import { z } from 'zod';

/**
 * The Payment Gateways module's contracts.
 *
 * Credentials are never returned by the service — only an indicator that they
 * are configured. The form collects fresh credentials on every edit; leaving the
 * provider's fields blank keeps the existing set.
 */

const NAME_MAX = 255;

export const PAYMENT_PROVIDER = {
  WINDCAVE: 'windcave',
  STRIPE: 'stripe',
  MANUAL: 'manual',
  WESTPAC: 'westpac',
} as const;

export type PaymentProvider = (typeof PAYMENT_PROVIDER)[keyof typeof PAYMENT_PROVIDER];

export const PROVIDER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: PAYMENT_PROVIDER.WINDCAVE, label: 'Windcave' },
  { value: PAYMENT_PROVIDER.STRIPE, label: 'Stripe' },
  { value: PAYMENT_PROVIDER.WESTPAC, label: 'Westpac' },
  { value: PAYMENT_PROVIDER.MANUAL, label: 'Manual' },
];

/**
 * One row in the gateway list.
 *
 * Only `id` is guaranteed, because the list endpoint may eventually honour
 * column projection. Every other field is treated as optional for the same
 * reason the properties row is: a hidden column must not break the parse.
 */
export const paymentGatewayRow = z.object({
  id: z.string(),
  tenantId: z.string().optional(),
  provider: z.string().optional(),
  name: z.string().optional(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  credentialsConfigured: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  version: z.number().optional(),
});

export type PaymentGatewayRow = z.infer<typeof paymentGatewayRow>;

/** The full gateway, as the detail screen and the edit form read it. */
export const paymentGatewayDetail = z.object({
  id: z.string(),
  tenantId: z.string(),
  provider: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  credentialsConfigured: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Echoed back so the edit form can round-trip it on save — it is the
  // optimistic lock, not decoration.
  version: z.number(),
});

export type PaymentGatewayDetail = z.infer<typeof paymentGatewayDetail>;

const basePaymentGatewayFormInput = z.object({
  provider: z.string().min(1, 'Choose a provider'),
  name: z
    .string()
    .trim()
    .min(1, 'A payment gateway needs a name')
    .max(NAME_MAX, `Keep the name under ${String(NAME_MAX)} characters`),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  // Windcave
  windcaveBaseUrl: z.string(),
  windcaveUsername: z.string(),
  windcaveAuthKey: z.string(),
  // Stripe
  stripePublishableKey: z.string(),
  stripeSecretKey: z.string(),
  // Westpac
  westpacMerchantId: z.string(),
  westpacApiKey: z.string(),
  westpacPassword: z.string(),
});

export type PaymentGatewayFormInput = z.infer<typeof basePaymentGatewayFormInput>;

const CREDENTIAL_FIELDS: Record<PaymentProvider, readonly (keyof PaymentGatewayFormInput)[]> = {
  [PAYMENT_PROVIDER.WINDCAVE]: ['windcaveBaseUrl', 'windcaveUsername', 'windcaveAuthKey'],
  [PAYMENT_PROVIDER.STRIPE]: ['stripePublishableKey', 'stripeSecretKey'],
  [PAYMENT_PROVIDER.WESTPAC]: ['westpacMerchantId', 'westpacApiKey', 'westpacPassword'],
  [PAYMENT_PROVIDER.MANUAL]: [],
};

const CREDENTIAL_LABELS: Readonly<Record<string, string>> = {
  windcaveBaseUrl: 'Base URL',
  windcaveUsername: 'Username',
  windcaveAuthKey: 'Auth Key',
  stripePublishableKey: 'Stripe publishable key',
  stripeSecretKey: 'Stripe secret key',
  westpacMerchantId: 'Westpac merchant ID',
  westpacApiKey: 'Westpac API key',
  westpacPassword: 'Westpac password',
};

function hasAny(
  values: PaymentGatewayFormInput,
  fields: readonly (keyof PaymentGatewayFormInput)[],
): boolean {
  for (const field of fields) {
    if (typeof values[field] === 'string' && values[field].trim() !== '') return true;
  }
  return false;
}

function validateProviderCredentials(values: PaymentGatewayFormInput, ctx: z.RefinementCtx): void {
  if (values.provider === '') return;

  if (!Object.values(PAYMENT_PROVIDER).includes(values.provider as PaymentProvider)) {
    ctx.addIssue({
      code: 'custom',
      path: ['provider'],
      message: 'Choose a recognised provider',
    });
    return;
  }

  const fields = CREDENTIAL_FIELDS[values.provider as PaymentProvider];

  // Credentials are all-or-nothing. Once the user starts a provider's set, the
  // whole set is required so the service can encrypt a complete blob. Leaving
  // every field blank on edit keeps the existing set.
  if (fields.length === 0 || !hasAny(values, fields)) return;

  for (const field of fields) {
    const value = values[field];
    if (typeof value !== 'string') continue;

    if (value.trim() === '') {
      const label = CREDENTIAL_LABELS[field] ?? field;
      ctx.addIssue({
        code: 'custom',
        path: [field],
        message: `${label} is required`,
      });
      continue;
    }

    if (field === 'windcaveBaseUrl') {
      const urlCheck = z.url().safeParse(value);
      if (!urlCheck.success) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: 'Base URL must be a valid URL',
        });
      }
    }
  }
}

/** What the create and edit forms collect. */
export const paymentGatewayFormInput = basePaymentGatewayFormInput.superRefine((values, ctx) => {
  validateProviderCredentials(values, ctx);
});

/** What the create endpoint returns. */
export const createdPaymentGateway = z.object({ id: z.string() });

export type CreatedPaymentGateway = z.infer<typeof createdPaymentGateway>;
